import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { ArrowLeft, Bell, Bot, CheckCircle2, Clock, Download, ImagePlus, Mail, MessageCircle, RefreshCw, Search, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { siteConfig } from "../../lib/siteConfig";
import { registerAdminPushNotifications } from "../../lib/adminPush";
import ImagePreviewModal from "../../components/ImagePreviewModal";
import LinkifiedText from "../../components/LinkifiedText";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";
import { VerifiedName } from "../../components/VerifiedBadge";
import { useSupportAttachments } from "../../lib/supportMedia";
import { SupportAttachmentPreviews, SupportVideo, supportMessageImages } from "../../components/SupportMedia";

const PLAYER_ONLINE_MS = 2 * 60 * 1000;
let supportAlertAudioContext = null;

function isPhoneLikeDevice() {
  if (typeof window === "undefined") return false;
  const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches;
  const touchPoints = navigator.maxTouchPoints || 0;
  const screenWidth = window.screen?.width || window.innerWidth;
  const screenHeight = window.screen?.height || window.innerHeight;
  const smallestScreenSide = Math.min(screenWidth, screenHeight);
  return Boolean((coarsePointer || touchPoints > 0) && smallestScreenSide <= 820);
}

export default function AdminSupport({ appMode = false }) {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiModePendingId, setAiModePendingId] = useState(null);
  const [globalAiSettings, setGlobalAiSettings] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const { attachments, setAttachments, select: selectAttachments, remove: removeAttachment, uploadAll, progress: uploadProgress, cancel: cancelUpload } = useSupportAttachments();
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const [phoneLayout, setPhoneLayout] = useState(() => isPhoneLikeDevice());
  const [unreadCount, setUnreadCount] = useState(0);
  const [imagePreview, setImagePreview] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => (
    typeof window !== "undefined" && window.localStorage.getItem("admin_support_notifications") === "on"
  ));
  const scrollerRef = useRef(null);
  const fileRef = useRef(null);
  const seenCustomerMessagesRef = useRef(new Set());
  const firstLoadRef = useRef(true);
  const aiModeRequestRef = useRef({ revision: 0, pending: false });

  const load = useCallback(async () => {
    if (aiModeRequestRef.current.pending) return;
    const revision = aiModeRequestRef.current.revision;
    const params = selectedId ? { ticket_id: selectedId } : undefined;
    const [{ data }, settingsResponse] = await Promise.all([
      api.get("/admin/support", { params }),
      api.get("/admin/support/ai-settings").catch(() => null),
    ]);
    // A response started before the toggle must not restore the old AI mode.
    if (aiModeRequestRef.current.pending || revision !== aiModeRequestRef.current.revision) return;
    setGlobalAiSettings(typeof settingsResponse?.data?.enabled === "boolean" ? settingsResponse.data : null);
    const list = Array.isArray(data) ? data : [];
    const newCustomerMessages = [];
    const totalUnread = totalTicketUnreadCount(list);

    list.forEach((ticket) => {
      normalizeMessages(ticket).forEach((message) => {
        if (message.sender !== "user") return;
        const key = messageKey(ticket, message);
        if (!seenCustomerMessagesRef.current.has(key)) {
          seenCustomerMessagesRef.current.add(key);
          if (!firstLoadRef.current) newCustomerMessages.push({ ticket, message });
        }
      });
    });

    if (firstLoadRef.current) {
      firstLoadRef.current = false;
    } else if (newCustomerMessages.length) {
      if (notificationsEnabled) {
        notifyAdmin(newCustomerMessages[0].ticket, newCustomerMessages[0].message, newCustomerMessages.length);
      }
    }

    setTickets(list);
    setUnreadCount(totalUnread);
    updateSupportTitle(totalUnread);
    setSelectedId((current) => (current && list.some((ticket) => ticket.id === current) ? current : null));
    setLastSync(new Date());
  }, [notificationsEnabled, selectedId]);

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, [load]);

  useAdminAutoRefresh(() => load());

  useEffect(() => {
    const updatePhoneLayout = () => setPhoneLayout(isPhoneLikeDevice());
    updatePhoneLayout();
    const pointerQuery = window.matchMedia?.("(pointer: coarse)");
    pointerQuery?.addEventListener?.("change", updatePhoneLayout);
    window.addEventListener("resize", updatePhoneLayout);
    window.addEventListener("orientationchange", updatePhoneLayout);
    return () => {
      pointerQuery?.removeEventListener?.("change", updatePhoneLayout);
      window.removeEventListener("resize", updatePhoneLayout);
      window.removeEventListener("orientationchange", updatePhoneLayout);
    };
  }, []);

  useEffect(() => {
    return () => updateSupportTitle(0);
  }, []);

  useEffect(() => {
    if (!appMode || !notificationsEnabled) return;
    registerAdminPushNotifications().catch(() => {});
  }, [appMode, notificationsEnabled]);

  const selected = useMemo(
    () => tickets.find((ticket) => ticket.id === selectedId) || null,
    [tickets, selectedId],
  );

  const visibleTickets = useMemo(() => {
    const term = search.trim().toLowerCase();
    const source = unreadOnly ? tickets.filter((ticket) => ticketUnreadCount(ticket) > 0) : tickets;
    if (!term) return source;
    return source.filter((ticket) => {
      const haystack = [
        customerName(ticket),
        displayEmail(ticket.email),
        ticket.subject,
        ticket.guest_id,
        ticket.status,
        messagePreview(ticket),
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(term);
    });
  }, [tickets, search, unreadOnly]);

  const messages = useMemo(() => normalizeMessages(selected), [selected]);
  const threadOpen = Boolean(selected && mobileThreadOpen);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [selectedId, messages.length]);

  const markTicketRead = useCallback(async (ticket) => {
    const ticketId = ticket?.id;
    const ticketUnread = ticketUnreadCount(ticket);
    if (!ticketId || ticketUnread <= 0) return;

    const readAt = new Date().toISOString();
    setTickets((current) => current.map((row) => (
      row.id === ticketId ? { ...row, unread_count: 0, admin_last_read_at: readAt } : row
    )));
    setUnreadCount((current) => {
      const next = Math.max(0, current - ticketUnread);
      updateSupportTitle(next);
      return next;
    });

    try {
      const revision = aiModeRequestRef.current.revision;
      const { data } = await api.post(`/admin/support/${ticketId}/read`, {}, { skipMaintenance: true });
      if (aiModeRequestRef.current.pending || revision !== aiModeRequestRef.current.revision) return;
      setTickets((current) => current.map((row) => (row.id === data.id ? data : row)));
    } catch {
      load().catch(() => {});
    }
  }, [load]);

  useEffect(() => {
    if (selected && ticketUnreadCount(selected) > 0) {
      markTicketRead(selected);
    }
  }, [markTicketRead, selected]);

  const enableNotifications = async () => {
    try {
      const nativePush = await registerAdminPushNotifications();
      if (nativePush.ok) {
        window.localStorage.setItem("admin_support_notifications", "on");
        setNotificationsEnabled(true);
        await unlockAlertSound();
        toast.success("Background support alerts enabled");
        return;
      }
    } catch {
      // Fall through to browser notification support below.
    }

    if (typeof window === "undefined" || !("Notification" in window)) {
      toast.error("Browser notifications are not supported here.");
      return;
    }
    try {
      const permission = window.Notification.permission === "granted"
        ? "granted"
        : await window.Notification.requestPermission();
      if (permission !== "granted") {
        const soundReady = await unlockAlertSound();
        if (soundReady) {
          window.localStorage.setItem("admin_support_notifications", "on");
          setNotificationsEnabled(true);
          toast.success("Sound alerts enabled. Browser notification permission is still blocked.");
          return;
        }
        toast.error("Please allow notifications in the browser to get support alerts.");
        return;
      }
      window.localStorage.setItem("admin_support_notifications", "on");
      setNotificationsEnabled(true);
      await unlockAlertSound();
      toast.success("Support alerts enabled");
    } catch {
      toast.error("Could not enable browser alerts.");
    }
  };

  const toggleAllAiReplies = async () => {
    if (!globalAiSettings?.configured || busy || aiModeRequestRef.current.pending) return;
    aiModeRequestRef.current.pending = true;
    aiModeRequestRef.current.revision += 1;
    setAiModePendingId("all-chats");
    setBusy(true);
    try {
      const { data } = await api.put("/admin/support/ai-settings", { enabled: !globalAiSettings.enabled });
      setGlobalAiSettings(data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      aiModeRequestRef.current.pending = false;
      aiModeRequestRef.current.revision += 1;
      setAiModePendingId(null);
      setBusy(false);
      load().catch(() => {});
    }
  };

  const toggleAiReplies = async () => {
    if (!selected || !globalAiSettings?.enabled || busy || aiModeRequestRef.current.pending || ["closed", "resolved"].includes(selected.status)) return;
    const ticketId = selected.id;
    const enabled = selected.ai_mode === "staff";
    aiModeRequestRef.current.pending = true;
    aiModeRequestRef.current.revision += 1;
    setAiModePendingId(ticketId);
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/support/${ticketId}/ai`, { enabled });
      setTickets((current) => current.map((ticket) => ticket.id === ticketId ? data : ticket));
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      aiModeRequestRef.current.pending = false;
      aiModeRequestRef.current.revision += 1;
      setAiModePendingId(null);
      setBusy(false);
      load().catch(() => {});
    }
  };

  const closeChat = async () => {
    if (!selected || busy || ["closed", "resolved"].includes(selected.status)) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/support/${selected.id}`, { status: "closed" });
      setTickets((current) => current.map((ticket) => ticket.id === data.id ? data : ticket));
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    const message = reply.trim();
    const picked = attachments;
    if ((!message && !picked.length) || !selected || busy || aiModeRequestRef.current.pending) return;
    setBusy(true);
    try {
      const attachmentIds = await uploadAll();
      const { data } = await api.post(`/admin/support/${selected.id}/messages`, {
        message,
        attachment_ids: attachmentIds,
      });
      picked.forEach((item) => {
        item.sent = true;
        if (item.dataUrl) URL.revokeObjectURL(item.dataUrl);
      });
      setReply("");
      setAttachments([]);
      setSelectedId(data.id);
      setTickets((current) => current.map((ticket) => (ticket.id === data.id ? data : ticket)));
      await load();
      toast.success("Reply sent");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const selectImage = async (files) => {
    const pickedFiles = files instanceof File ? [files] : files;
    if (!pickedFiles?.length || busy) return;
    try {
      selectAttachments(pickedFiles);
    } catch (e) {
      toast.error(e.message);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className={`admin-support-app flex h-[100dvh] min-h-0 max-h-[100dvh] flex-col overflow-hidden bg-[#08090d] text-white ${phoneLayout ? "admin-support-phone-layout" : ""}`}>
      <header className="shrink-0 border-b border-white/10 bg-[#090a0f] px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-3">
              {threadOpen && (
                <button
                  type="button"
                  onClick={() => setMobileThreadOpen(false)}
                  className="admin-support-mobile-back grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-300 hover:bg-white/5 md:hidden"
                  aria-label="Back to conversations"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <MessageCircle className="h-7 w-7 shrink-0 text-[#6EA8D8]" />
              <h1 className="truncate text-2xl font-semibold tracking-tight sm:text-3xl">
                {appMode ? `${siteConfig.name} Support` : "Chat"}
              </h1>
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                Live
              </span>
              {appMode && (
                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#2F6FA5]/30 bg-[#2F6FA5]/10 px-3 py-1 text-xs text-[#B7D7F2]">
                  <ShieldCheck className="h-3 w-3" />
                  Admin only
                </span>
              )}
              {unreadCount > 0 && (
                <span className="inline-flex shrink-0 items-center rounded-full border border-[#2F6FA5]/30 bg-[#2F6FA5]/15 px-2.5 py-1 text-xs text-[#B7D7F2]">
                  {unreadCount} new
                </span>
              )}
            </div>
            <p className="mt-1 hidden truncate text-sm text-slate-500 sm:block">
              {appMode ? "Standalone admin support app" : `${tickets.length} conversations`}
              {appMode ? ` - ${tickets.length} conversations` : ""}
              {lastSync ? ` - synced ${lastSync.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
            </p>
          </div>

          <div className="flex max-w-full items-center gap-2 overflow-x-auto pb-1">
            {appMode && (
              <button
                type="button"
                onClick={() => navigate("/admin/dashboard")}
                className="shrink-0 rounded-full border border-white/10 px-3 py-2 text-xs font-medium text-slate-300 transition hover:bg-white/5"
                title="Back to admin"
              >
                <span className="inline-flex items-center gap-2">
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={enableNotifications}
              className={`shrink-0 rounded-full border px-3 py-2 text-xs font-medium transition ${
                notificationsEnabled
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                  : "border-white/10 text-slate-300 hover:bg-white/5"
              }`}
              title="Enable browser alerts"
            >
              <span className="inline-flex items-center gap-2">
                <Bell className="h-3.5 w-3.5" />
                {notificationsEnabled ? "Alerts on" : "Enable alerts"}
              </span>
            </button>
            {appMode && (
              <a
                href={siteConfig.adminAppDownloadUrl}
                download="FEA-Slots-Admin.apk"
                className="hidden shrink-0 rounded-full border border-[#2F6FA5]/30 px-3 py-2 text-xs font-medium text-[#B7D7F2] transition hover:bg-[#2F6FA5]/10 sm:inline-flex"
                title="Download FEA Slots Admin app"
              >
                <span className="inline-flex items-center gap-2">
                  <Download className="h-3.5 w-3.5" />
                  Download Admin App
                </span>
              </a>
            )}
            <button
              type="button"
              onClick={load}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 text-slate-300 hover:bg-white/5"
              title="Refresh now"
              aria-label="Refresh conversations"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="shrink-0 border-b border-white/10 bg-[#0c0d12] px-4 py-1 sm:px-6">
        <button
          type="button"
          role="switch"
          aria-label="AI replies for all chats"
          aria-checked={Boolean(globalAiSettings?.enabled)}
          aria-busy={aiModePendingId === "all-chats"}
          disabled={busy || !globalAiSettings?.configured}
          onClick={toggleAllAiReplies}
          title="AI replies for all chats"
          className="inline-flex min-h-[44px] max-w-full items-center gap-3 rounded-md text-sm text-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6EA8D8] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="text-left">AI replies <span className="text-slate-400">- All chats</span></span>
          <span aria-hidden="true" className={`relative h-6 w-10 shrink-0 rounded-full transition-colors ${globalAiSettings?.enabled ? "bg-emerald-600" : "bg-slate-600"}`}>
            <span className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform ${globalAiSettings?.enabled ? "translate-x-4" : "translate-x-0"}`} />
          </span>
          <span aria-hidden="true" className="min-w-6 text-left text-xs">{globalAiSettings ? globalAiSettings.enabled ? "On" : "Off" : "Unavailable"}</span>
        </button>
      </div>

      <div className="admin-support-panels grid min-h-0 flex-1 overflow-hidden md:grid-cols-[398px_minmax(0,1fr)]">
        <aside
          data-thread-open={threadOpen ? "true" : "false"}
          className={`admin-support-sidebar ${threadOpen ? "hidden" : "flex"} min-h-0 min-w-0 flex-col overflow-hidden border-r border-white/10 bg-[#0a0b10] md:flex`}
        >
          <div className="shrink-0 space-y-2 border-b border-white/10 p-3 sm:p-4">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-10 w-full rounded-full border border-white/10 bg-black/20 pl-10 pr-4 text-sm outline-none placeholder:text-slate-500 focus:border-[#2F6FA5]/50 sm:h-11"
                placeholder="Search players..."
              />
            </label>
            <button
              type="button"
              onClick={() => setUnreadOnly((current) => !current)}
              aria-pressed={unreadOnly}
              aria-label={`Show ${unreadCount} unread conversations`}
              title="Show only conversations with unread messages"
              data-testid="support-unread-filter"
              className={`inline-flex min-h-10 items-center gap-2 rounded-full border px-3 text-sm font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6EA8D8] ${
                unreadOnly
                  ? "border-[#2F6FA5]/50 bg-[#2F6FA5]/15 text-[#B7D7F2]"
                  : "border-white/10 text-slate-300 hover:bg-white/5"
              }`}
            >
              <Mail className="h-4 w-4" aria-hidden="true" />
              <span>Unread</span>
              <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs tabular-nums">{unreadCount}</span>
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[calc(4.5rem+env(safe-area-inset-bottom))] sm:pb-0">
            {visibleTickets.map((ticket) => {
              const active = ticket.id === selectedId;
              return (
                <button
                  key={ticket.id}
                  type="button"
                  onClick={() => {
                    setSelectedId(ticket.id);
                    setMobileThreadOpen(true);
                    markTicketRead(ticket);
                  }}
                  data-testid={`ticket-row-${ticket.id}`}
                  className={`w-full border-b border-white/[0.06] px-3 py-4 text-left transition sm:px-4 ${
                    active ? "bg-white/[0.07]" : "hover:bg-white/[0.04]"
                  }`}
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <Avatar ticket={ticket} />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-start justify-between gap-3">
                        <p className="flex min-w-0 items-center gap-2 truncate text-[15px] font-semibold leading-tight text-white">
                          <VerifiedName name={customerName(ticket)} user={ticket} />
                          <PlayerPresence ticket={ticket} />
                        </p>
                        <span className="shrink-0 text-[11px] font-medium text-slate-500">
                          {formatRelativeTime(ticket.last_message_at || ticket.updated_at || ticket.created_at)}
                        </span>
                      </div>
                      <p className="mt-1 truncate text-sm text-slate-400">{messagePreview(ticket)}</p>
                      <PlayerLastOnline ticket={ticket} />
                    </div>
                  </div>
                </button>
              );
            })}
            {visibleTickets.length === 0 && (
              <div className="grid min-h-[280px] place-items-center px-6 text-center text-sm text-slate-500">
                {unreadOnly ? "No unread conversations." : "No player conversations found."}
              </div>
            )}
          </div>
        </aside>

        <main
          data-thread-open={threadOpen ? "true" : "false"}
          className={`admin-support-thread ${threadOpen ? "flex" : "hidden md:flex"} min-h-0 min-w-0 flex-col overflow-hidden bg-[#090a0f]`}
        >
          {selected ? (
            <ConversationPanel
              attachments={attachments}
              uploadProgress={uploadProgress}
              cancelUpload={cancelUpload}
              aiModePendingId={aiModePendingId}
              globalAiEnabled={globalAiSettings?.enabled ?? null}
              busy={busy}
              fileRef={fileRef}
              messages={messages}
              onImagePreview={setImagePreview}
              reply={reply}
              scrollerRef={scrollerRef}
              selected={selected}
              selectImage={selectImage}
              sendReply={sendReply}
              removeAttachment={removeAttachment}
              setMobileThreadOpen={setMobileThreadOpen}
              setReply={setReply}
              closeChat={closeChat}
              toggleAiReplies={toggleAiReplies}
            />
          ) : (
            <EmptyConversation />
          )}
        </main>
      </div>
      <ImagePreviewModal image={imagePreview} onClose={() => setImagePreview(null)} />
    </div>
  );
}

function ConversationPanel({
  attachments,
  uploadProgress,
  cancelUpload,
  aiModePendingId,
  globalAiEnabled,
  busy,
  fileRef,
  messages,
  onImagePreview,
  reply,
  scrollerRef,
  selected,
  selectImage,
  sendReply,
  removeAttachment,
  setMobileThreadOpen,
  setReply,
  closeChat,
  toggleAiReplies,
}) {
  const chatClosed = ["closed", "resolved"].includes(selected.status);
  const aiEnabled = Boolean(globalAiEnabled && selected.ai_mode !== "staff");
  const handlePaste = (event) => {
    const file = imageFileFromClipboard(event.clipboardData);
    if (!file) return;
    event.preventDefault();
    selectImage(file);
  };

  return (
    <>
      <div className="shrink-0 border-b border-white/10 bg-[#0c0d12] px-4 py-3">
        <div className="flex min-w-0 items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileThreadOpen(false)}
              className="admin-support-mobile-back grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 text-slate-300 hover:bg-white/5 md:hidden"
              aria-label="Back to conversations"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <Avatar ticket={selected} />
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <h2 className="flex min-w-0 items-center gap-2 truncate text-lg font-semibold">
                  <VerifiedName name={customerName(selected)} user={selected} />
                  <PlayerPresence ticket={selected} showLabel />
                </h2>
              </div>
              <p className="mt-0.5 flex min-w-0 items-center gap-1.5 truncate text-xs text-slate-500">
                <Mail className="h-3 w-3 shrink-0" />
                <span className="truncate">{displayEmail(selected.email)}</span>
              </p>
              <PlayerLastOnline ticket={selected} detailed />
            </div>
          </div>
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-white/10 px-4 py-2">
        <div className="flex min-w-0 flex-col items-start gap-1">
          <button
            type="button"
            role="switch"
            aria-label="AI replies for this chat"
            aria-checked={aiEnabled}
            aria-busy={aiModePendingId === selected.id}
            disabled={busy || chatClosed || !globalAiEnabled}
            onClick={toggleAiReplies}
            title="AI replies for this chat"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-md text-xs text-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6EA8D8] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span>AI replies</span>
            <span aria-hidden="true" className={`relative h-6 w-10 shrink-0 rounded-full transition-colors ${aiEnabled ? "bg-emerald-600" : "bg-slate-600"}`}>
              <span className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform ${aiEnabled ? "translate-x-4" : "translate-x-0"}`} />
            </span>
            <span aria-hidden="true" className="w-6 text-left">{aiEnabled ? "On" : "Off"}</span>
          </button>
          <SupportAiStatus ticket={selected} globalAiEnabled={globalAiEnabled} />
        </div>
        <button
          type="button"
          onClick={closeChat}
          disabled={busy || ["closed", "resolved"].includes(selected.status)}
          className="inline-flex h-8 shrink-0 items-center gap-2 rounded-md border border-white/10 px-3 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-50"
        >
          <CheckCircle2 className="h-4 w-4" />
          {["closed", "resolved"].includes(selected.status) ? "Closed" : "Close chat"}
        </button>
      </div>

      <div ref={scrollerRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6">
        {messages.length ? (
          <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
            {messages.map((msg) => (
              <ChatBubble
                key={msg.id || `${msg.sender}-${msg.created_at}-${msg.message}`}
                msg={msg}
                onImagePreview={onImagePreview}
                player={selected}
              />
            ))}
          </div>
        ) : (
          <div className="grid h-full place-items-center text-center text-sm text-slate-500">
            No messages yet.
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-white/10 bg-[#0c0d12] p-3 sm:p-4">
        <div className="mx-auto w-full max-w-5xl">
          <SupportAttachmentPreviews attachments={attachments} progress={uploadProgress} busy={busy}
            onRemove={removeAttachment} onCancel={cancelUpload} />

          <div className="flex items-end gap-2">
            <input ref={fileRef} type="file" accept="image/*,video/*" multiple disabled={busy} className="hidden" onChange={(e) => selectImage(e.target.files)} />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              title="Add photo or video"
              aria-label="Add photo or video"
              className="grid h-[54px] w-[54px] shrink-0 place-items-center rounded-2xl border border-white/10 bg-black/30 text-slate-200 hover:bg-white/5 disabled:opacity-50"
            >
              <ImagePlus className="h-4 w-4" />
            </button>
            <textarea
              disabled={busy}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendReply();
                }
              }}
              rows={2}
              data-testid="ticket-reply"
              className="min-w-0 flex-1 resize-none rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-sm outline-none placeholder:text-slate-500 focus:border-[#2F6FA5]/50"
              placeholder="Type a message... or Ctrl+V screenshot"
            />
            <button
              type="button"
              onClick={sendReply}
              disabled={busy || (!reply.trim() && !attachments.length)}
              data-testid="send-agent-reply"
              className="grid h-[54px] w-[58px] shrink-0 place-items-center rounded-2xl bg-[#2F6FA5] text-white hover:bg-[#4E91C8] disabled:opacity-50"
              aria-label="Send reply"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function SupportAiStatus({ ticket, globalAiEnabled }) {
  const [now, setNow] = useState(Date.now);
  const resetAt = ["closed", "resolved"].includes(ticket.status) ? Date.parse(ticket.ai_reset_at || "") : NaN;
  useEffect(() => {
    if (!Number.isFinite(resetAt)) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [resetAt]);
  const resetting = Number.isFinite(resetAt);
  const seconds = Math.max(0, Math.ceil((resetAt - now) / 1000));
  const label = globalAiEnabled === null ? "AI status unavailable" : !globalAiEnabled ? "AI paused for all chats" : resetting
    ? seconds > 0 ? `AI reset in ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}` : "Resetting to AI"
    : ticket.ai_mode === "staff" ? "Staff review" : ticket.ai_staff_review_required ? "AI + staff review" : "AI support";
  const Icon = resetting ? Clock : ticket.ai_mode === "staff" ? MessageCircle : Bot;
  return (
    <span role="status" aria-label="Support mode" className="inline-flex min-w-0 items-center gap-2 text-xs text-slate-300">
      <Icon className="h-4 w-4 shrink-0" />
      <span>{label}</span>
    </span>
  );
}

function EmptyConversation() {
  return (
    <div className="grid min-h-0 flex-1 place-items-center px-6 text-center">
      <div className="max-w-md text-slate-400">
        <MessageCircle className="mx-auto mb-4 h-10 w-10 text-slate-700" />
        <p className="text-lg leading-relaxed">
          Select a player conversation on the left - or wait for the first message.
          Posts you send go out as {siteConfig.name} support.
        </p>
      </div>
    </div>
  );
}

function Avatar({ ticket }) {
  const src = avatarUrl(ticket);
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="h-11 w-11 shrink-0 rounded-full border border-[#2F6FA5]/30 object-cover"
      />
    );
  }
  return (
    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#3a280a] text-sm font-bold text-[#6EA8D8]">
      {initialFor(ticket)}
    </div>
  );
}

function playerPresenceTime(ticket) {
  return ticket?.last_seen_at || ticket?.last_user_seen_at || "";
}

function isPlayerOnline(ticket) {
  const value = playerPresenceTime(ticket);
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return Date.now() - date.getTime() <= PLAYER_ONLINE_MS;
}

function PlayerPresence({ ticket, showLabel = false }) {
  const online = isPlayerOnline(ticket);
  const label = online ? "Online" : "Offline";
  return (
    <span
      title={label}
      aria-label={label}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full ${showLabel ? "border px-2 py-0.5 text-[11px] font-bold" : ""} ${
        online
          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
          : "border-red-500/30 bg-red-500/10 text-red-300"
      }`}
    >
      <span className={`h-3 w-3 rounded-full ring-2 ring-black/40 ${online ? "bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.8)]" : "bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.7)]"}`} />
      {showLabel && <span>{label}</span>}
    </span>
  );
}

function PlayerLastOnline({ ticket, detailed = false }) {
  const raw = playerPresenceTime(ticket);
  const date = raw ? new Date(raw) : null;
  const available = date && !Number.isNaN(date.getTime());
  const absolute = available ? date.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  }) : "";
  const minutes = available ? Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000)) : 0;
  const count = minutes < 60 ? minutes : minutes < 1440 ? Math.floor(minutes / 60) : Math.floor(minutes / 1440);
  const unit = minutes < 60 ? "minute" : minutes < 1440 ? "hour" : "day";
  const relative = !available ? "Not available" : minutes < 1 ? "Just now" : `${count} ${unit}${count === 1 ? "" : "s"} ago`;
  return (
    <span
      data-testid={`last-online-${detailed ? "detail" : "list"}-${ticket.id}`}
      className="mt-1 flex min-w-0 items-start gap-1.5 text-[11px] leading-4 text-slate-400"
    >
      <Clock aria-hidden="true" className="mt-0.5 h-3 w-3 shrink-0" />
      <span className="min-w-0 break-words">
        <span>Last online: {relative}</span>
        {available && (
          <time dateTime={date.toISOString()} title={absolute} className={detailed ? "block" : "sr-only"}>
            {absolute}
          </time>
        )}
      </span>
    </span>
  );
}

function normalizeMessages(ticket) {
  if (!ticket) return [];
  if (Array.isArray(ticket.messages) && ticket.messages.length) return ticket.messages;
  const out = [];
  if (ticket.message) {
    out.push({ id: `${ticket.id}-user`, sender: "user", message: ticket.message, created_at: ticket.created_at });
  }
  if (ticket.admin_reply) {
    out.push({ id: `${ticket.id}-agent`, sender: "agent", message: ticket.admin_reply, created_at: ticket.updated_at || ticket.created_at });
  }
  return out;
}

function lastMessage(ticket) {
  const messages = normalizeMessages(ticket);
  return messages[messages.length - 1] || null;
}

function messagePreview(ticket) {
  const last = lastMessage(ticket);
  if (!last) return ticket?.subject || "No messages yet";
  const prefix = last.sender === "agent" ? (last.is_ai ? (last.ai_generated ? "AI Support: " : "Automated Support: ") : last.agent_target ? `You -> ${last.agent_target}: ` : "You: ") : "";
  const target = last.sender !== "agent" && last.agent_target ? `To ${last.agent_target}: ` : "";
  if (last.image_url && !last.message) return `${prefix}${target}Photo`;
  if (last.video_url && !last.message) return `${prefix}${target}Video`;
  return `${prefix}${target}${last.message || "Photo"}`;
}

function messageKey(ticket, message) {
  return message.id || `${ticket.id}:${message.sender}:${message.created_at || ""}:${message.message || ""}:${message.image_url || ""}`;
}

function ticketUnreadCount(ticket) {
  const count = Number(ticket?.unread_count || 0);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
}

function totalTicketUnreadCount(tickets) {
  return (Array.isArray(tickets) ? tickets : []).reduce((total, ticket) => total + ticketUnreadCount(ticket), 0);
}

function updateSupportTitle(count) {
  if (typeof document === "undefined") return;
  document.title = count > 0 ? `(${count}) Chat - ${siteConfig.name}` : siteConfig.name;
}

function notifyAdmin(ticket, message, count) {
  playAlertTone();
  if (typeof window === "undefined" || !("Notification" in window) || window.Notification.permission !== "granted") return;
  try {
    const customer = customerName(ticket);
    const body = message.message?.trim() || (message.video_url ? "Sent a video" : message.image_url ? "Sent a photo" : "New support message");
    const notification = new window.Notification(
      count > 1 ? `${count} new support messages` : `New message from ${customer}`,
      {
        body: body.slice(0, 140),
        icon: "/icon-192.png",
        tag: `support-${ticket.id}`,
      },
    );
    notification.onclick = () => {
      window.focus();
      window.location.assign("/admin/support-app");
    };
  } catch {
    // Browser notifications can fail silently in some mobile shells.
  }
}

function supportAudioContext() {
  if (typeof window === "undefined") return null;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return null;
  if (!supportAlertAudioContext || supportAlertAudioContext.state === "closed") {
    supportAlertAudioContext = new AudioContext();
  }
  return supportAlertAudioContext;
}

async function unlockAlertSound() {
  try {
    const context = supportAudioContext();
    if (!context) return false;
    if (context.state === "suspended") await context.resume();
    playAlertTone();
    return true;
  } catch {
    return false;
  }
}

function playAlertTone() {
  if (typeof window === "undefined") return;
  try {
    const context = supportAudioContext();
    if (!context) return;
    if (context.state === "suspended") context.resume().catch(() => {});
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.25);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.28);
  } catch {
    // Audio is best-effort; notifications still work without it.
  }
}

function displayEmail(email) {
  if (!email || email.endsWith(`@${siteConfig.guestEmailDomain}`)) return "Guest visitor";
  return email;
}

function customerName(ticket) {
  if (!ticket) return "Guest";
  return ticket.name || ticket.username || displayEmail(ticket.email) || "Guest";
}

function avatarUrl(ticket) {
  if (!ticket) return "";
  return ticket.avatar_url || ticket.profile_image || ticket.profile_pic || ticket.photo_url || "";
}

function initialFor(ticket) {
  const name = customerName(ticket).trim();
  return (name[0] || "G").toUpperCase();
}

function formatRelativeTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return "now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

function supportImageFilename(name) {
  const cleaned = String(name || "support-photo.jpg")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, "-");
  if (!cleaned) return "support-photo.jpg";
  return /\.[a-z0-9]{2,5}$/i.test(cleaned) ? cleaned : `${cleaned}.jpg`;
}

function triggerImageDownload(href, filename) {
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

async function downloadSupportImage(src, name) {
  if (!src) return;
  const filename = supportImageFilename(name);
  try {
    const response = await fetch(src, { credentials: "include" });
    if (!response.ok) throw new Error("Image download failed");
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    triggerImageDownload(blobUrl, filename);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (error) {
    triggerImageDownload(src, filename);
  }
}

function ChatBubble({ msg, onImagePreview, player }) {
  const isAgent = msg.sender === "agent";
  const target = msg.agent_target ? String(msg.agent_target).toUpperCase() : "";
  const imageName = msg.image_name || "support-photo.jpg";
  return (
    <div className={`flex ${isAgent ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[86%] rounded-[22px] border px-4 py-3 text-sm sm:max-w-[72%] ${
        isAgent
          ? "rounded-br-md border-[#2F6FA5] bg-[#2F6FA5] text-white"
          : "rounded-bl-md border-white/10 bg-white/[0.07] text-white"
      }`}>
        <div className={`mb-1 flex items-center gap-1 text-[10px] ${isAgent ? "text-black/55" : "text-white/55"}`}>
          {isAgent ? (
            msg.is_ai ? (msg.ai_generated ? "AI Support" : "Automated Support") : target ? `Agent -> ${target}` : "Agent"
          ) : (
            <>
              <VerifiedName
                className="[&>svg]:h-3.5 [&>svg]:w-3.5"
                name={customerName(player)}
                user={player}
              />
              {target ? <span>{`-> ${target}`}</span> : null}
            </>
          )}
        </div>
        {supportMessageImages(msg).map((image, index) => (
          <div key={`${image.image_url}-${index}`} className="relative mb-2 max-w-full rounded-xl bg-black/20">
            <button
              type="button"
              onClick={() => onImagePreview?.({ src: image.image_url, name: image.image_name || imageName })}
              className="block max-w-full cursor-zoom-in rounded-xl text-left outline-none ring-[#2F6FA5]/60 focus:ring-2"
              aria-label="Open image preview"
            >
              <img src={image.image_url} alt={image.image_name || imageName} className="max-h-64 w-full rounded-xl object-contain" />
            </button>
            <button
              type="button"
              onClick={() => downloadSupportImage(image.image_url, image.image_name || imageName)}
              className="absolute right-2 top-2 grid h-9 w-9 place-items-center rounded-full border border-white/20 bg-black/75 text-white shadow-lg hover:bg-black focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/70"
              title="Download image"
              aria-label="Download image"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        ))}
        <SupportVideo message={msg} />
        {msg.message && (
          <div className="whitespace-pre-wrap break-words leading-relaxed">
            <LinkifiedText
              text={msg.message}
              linkClassName="font-semibold text-white underline underline-offset-2 hover:opacity-80"
            />
          </div>
        )}
        {msg.created_at && (
          <div className={`mt-2 text-right text-[10px] ${isAgent ? "text-black/45" : "text-white/35"}`}>
            {formatRelativeTime(msg.created_at)}
          </div>
        )}
      </div>
    </div>
  );
}

function imageFileFromClipboard(clipboardData) {
  if (!clipboardData) return null;
  const files = Array.from(clipboardData.files || []);
  const file = files.find((item) => item?.type?.startsWith("image/"));
  if (file) return file;

  const items = Array.from(clipboardData.items || []);
  const imageItem = items.find((item) => item.kind === "file" && item.type?.startsWith("image/"));
  return imageItem?.getAsFile?.() || null;
}
